import {
  GridColDef,
  GridPaginationModel,
  GridSortModel,
  GridValidRowModel,
  GridColumnVisibilityModel,
} from "@mui/x-data-grid-premium";
import { CustomGridColDef } from "components/DataGrid";
import { omit } from "lodash-es";
import { useCallback, useMemo } from "react";
import { useGridPersistence } from "./useGridPersistence";
import { useOmnisearchInput } from "./useOmnisearchInput";
import { usePage } from "./usePage";

export type FilterKey = {
  key: string;
  header: string;
  filterKeyType?: FilterKeyType;
  HelpTooltip?: React.ComponentType;
  enumValues?: string[];
  enumPresentationFunction?: (enumValue: string) => string;
  dateRangeShortcuts?: boolean;
  disableFilterWhen?: {
    field: string;
    value: string | boolean;
  };
  nullCheckboxLabel?: string;
};

export type FilterKeyType =
  | "boolean"
  | "enum"
  | "enumAutocomplete"
  | "facility"
  | "wasteFacility"
  | "multipleFacility"
  | "wasteMultipleFacility"
  | "multipleTenant"
  | "multipleUser"
  | "number"
  | "stateOfMatter"
  | "tenant"
  | "text"
  | "time"
  | "date"
  // Tag types - must match TagFilterType in FilterPopover.tsx
  | "facilityTags"
  | "taskTags"
  | "workflowTags"
  | "wasteTags";

export type OmnisearchGridColDef<
  Row extends GridValidRowModel = Record<string, any>
> = CustomGridColDef<Row> & {
  filterKeyType?: FilterKeyType;
  enumValues?: string[];
  enumPresentationFunction?: (enumValue: string) => string;
  HelpTooltip?: React.ComponentType;
  dateRangeShortcuts?: boolean;
  disableFilterWhen?: {
    field: string;
    value: string | boolean;
  };
};

interface UseOmnisearchDatagridProps<Row extends GridValidRowModel> {
  persistenceKey: string;
  isURLDriven?: boolean;
  initialSortModel?: GridSortModel;
  initialPageSize?: number;
  initialColumnVisibilityModel?: GridColumnVisibilityModel;
  isMobile?: boolean;
  columns?: OmnisearchGridColDef<Row>[];
  initialFilters?: { field: string; value: string | boolean }[];
  additionalFilters?: FilterKey[];
  excludeFilters?: string[];
}

export function useOmnisearchDatagrid<
  Row extends GridValidRowModel = Record<string, any>
>({
  persistenceKey,
  isURLDriven = true,
  initialSortModel = [],
  initialPageSize = 25,
  initialColumnVisibilityModel,
  isMobile,
  columns: columnsInput = [],
  initialFilters = [],
  additionalFilters = [],
  excludeFilters = [],
}: UseOmnisearchDatagridProps<Row>) {
  const {
    omnisearch,
    debouncedOmnisearch,
    getOmnisearchValue,
    createUrlFromOmnisearchKeyValues,
    setOmnisearch,
    setOmnisearchImmediate,
    editOmnisearchKeyValues,
    removeOmnisearchKeyValue,
  } = useOmnisearchInput({ isURLDriven, initialFilters });

  const { page, setPage } = usePage({ isURLDriven });

  const {
    pageSize,
    sortModel,
    columnVisibilityModel,
    columnWidthModel,
    setPageSize,
    setSortModel,
    resetSortModel,
    setColumnVisibilityModel,
    setColumnWidthModel,
    unsetColumnWidthModel,
    resetColumnSettings,
  } = useGridPersistence({
    persistenceKey,
    initialSortModel,
    initialPageSize,
    initialColumnVisibilityModel,
    isMobile,
  });

  const paginationModel: GridPaginationModel = useMemo(
    () => ({ page, pageSize }),
    [page, pageSize]
  );

  const setPaginationModel = useCallback(
    (model: GridPaginationModel) => {
      setPage(model.page);
      setPageSize(model.pageSize);
    },
    [setPage, setPageSize]
  );

  const columns = useMemo(
    () =>
      columnsInput.map<GridColDef<Row>>(
        (col) =>
          ({
            ...omit(col, [
              "omnisearchKey",
              "omnisearchFilterKeyType",
              "omnisearchEnumValues",
              "omnisearchHelpTooltip",
              "width",
              "flex",
            ]),
            width: columnWidthModel[col.field] ?? col.width,
            flex: columnWidthModel[col.field] ? undefined : col.flex,
          } as GridColDef<Row>)
      ),
    [columnsInput, columnWidthModel]
  );

  const columnFilterKeys = useMemo(() => {
    return columnsInput
      .filter(
        (col) =>
          !excludeFilters.includes(col.field) &&
          col.filterable !== false &&
          col.type !== "actions"
      )
      .reduce<FilterKey[]>((keys, col) => {
        keys.push({
          header: col.headerName ?? col.field,
          key: col.field,
          filterKeyType: col.filterKeyType,
          enumValues: col.enumValues,
          HelpTooltip: col.HelpTooltip,
          enumPresentationFunction: col.enumPresentationFunction,
          dateRangeShortcuts: col.dateRangeShortcuts,
          disableFilterWhen: col.disableFilterWhen,
        });
        return keys;
      }, [])
      .concat(additionalFilters);
  }, [columnsInput, excludeFilters, additionalFilters]);

  return {
    omnisearch,
    debouncedOmnisearch,
    setOmnisearch,
    setOmnisearchImmediate,
    getOmnisearchValue,
    createUrlFromOmnisearchKeyValues,
    editOmnisearchKeyValues,
    removeOmnisearchKeyValue,
    page,
    pageSize,
    paginationModel,
    setPaginationModel,
    sortModel,
    setSortModel,
    resetSortModel,
    columns,
    columnVisibilityModel,
    columnWidthModel,
    setColumnVisibilityModel,
    setColumnWidthModel,
    unsetColumnWidthModel,
    resetColumnSettings,
    columnFilterKeys,
  };
}
